import {
  BookCheck,
  BookOpen,
  CircleCheck,
  ClipboardCheck,
  HelpCircle,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Error,
  MultiSkeleton,
  PageContainer,
  PageTitle,
  Placeholder,
} from '@/components'
import { Link, createFileRoute } from '@tanstack/react-router'

import { GetLessonListItem } from '@/components/LessonListItem/GetLessonListItem'
import type { Status } from '@/client'
import { getAllUserLessonsQueryOptions } from '@/requestObjects'
import { useAuthHeaders } from '@/providers'
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_authRoutes/_appWrapper/')({
  component: DashboardComponent,
})

function DashboardComponent() {
  const { t } = useTranslation()

  const headers = useAuthHeaders()
  const {
    data: userLessons = [],
    isLoading: userLessonsLoading,
    isError,
  } = useQuery({
    ...getAllUserLessonsQueryOptions({ headers }),
  })

  const stats = useMemo(() => {
    // Use reducer to count all statuses in a single pass

    const { in_progress, req_feedback, complete, ready } = userLessons.reduce(
      (acc, lesson) => {
        switch (lesson.status) {
          case 'in_progress':
            acc.in_progress++
            break
          case 'complete':
            if (!lesson.engaging_feedback || !lesson.objectives_feedback) {
              acc.req_feedback++
            } else {
              acc.complete++
            }
            break
          case 'ready':
            acc.ready++
            break
        }
        return acc
      },
      {
        ['in_progress']: 0,
        ['complete']: 0,
        req_feedback: 0,
        ['ready']: 0,
      } as Record<Status | 'req_feedback', number>,
    )

    return [
      {
        title: t('dashboard.stats.totalMindMapInProgress'),
        value: in_progress,
        icon: BookOpen,
        description: t('dashboard.stats.lessonPlansInProgress'),
      },
      {
        title: t('dashboard.stats.totalLessonsPlanned'),
        value: ready,
        icon: BookCheck,
        description: t('dashboard.stats.lessonPlansCreated'),
      },
      {
        title: t('dashboard.stats.reviewsOutstanding'),
        value: req_feedback,
        icon: ClipboardCheck,
        description: t('dashboard.stats.lessonsWithFeedbackOutstanding'),
      },
      {
        title: t('dashboard.stats.complete'),
        value: complete,
        icon: CircleCheck,
        description: t('dashboard.stats.taughtAndReflectedLessons'),
      },
    ]
  }, [userLessons, t])

  const quickActions = useMemo(
    () => [
      {
        title: t('dashboard.quickActions.createNewLesson.title'),
        description: t('dashboard.quickActions.createNewLesson.description'),
        icon: BookOpen,
        link: { to: '/lesson-planning' },
      },
      {
        title: t('dashboard.quickActions.teacherGuidelines.title'),
        description: t('dashboard.quickActions.teacherGuidelines.description'),
        icon: HelpCircle,
        link: { to: '/teacher-guidelines' },
      },
    ],
    [t],
  )

  return (
    <PageContainer>
      <div className="space-y-6">
        <PageTitle
          title={t('dashboard.title')}
          subtitle={t('dashboard.subtitle')}
        />

        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
          {stats.map((stat) => {
            const Icon = stat.icon
            return (
              <Card
                key={stat.title}
                className="gap-0 bg-primary-100 dark:bg-gray-950 relative overflow-hidden  shadow-none border-primary-400 dark:border-primary-800"
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 xl:pb-1">
                  <CardTitle className="text-sm xl:text-base font-medium">
                    {stat.title}
                  </CardTitle>
                  <div className="border-2 border-dashed border-primary-800 rounded-full w-30 h-30 absolute -top-2 -right-2 rtl:-left-2 rtl:right-auto translate-x-1/2 -translate-y-1/2 rtl:-translate-x-1/2" />
                  <div className="p-2 bg-yellow-500 text-gray-50 dark:bg-yellow-700 rounded-full z-10 shadow-lg">
                    <Icon className="h-5 w-5" />
                  </div>
                </CardHeader>
                <CardContent className="">
                  <div className="text-2xl lg:text-3xl xl:text-4xl font-bold">
                    {stat.value}
                  </div>
                  <p className="text-xs text-gray-700 dark:text-gray-300">
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            )
          })}
        </div>

        <div className="grid gap-4">
          <h3 className="text-lg font-medium">
            {t('dashboard.quickActions.title')}
          </h3>

          <nav className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4">
            {quickActions.map((action) => (
              <Link
                key={action.link.to}
                {...action.link}
                className="w-full h-full p-3 rounded-lg border bg-white dark:bg-gray-800  hover:bg-primary-100 dark:hover:bg-accent transition-colors flex flex-row gap-4 items-center"
              >
                <action.icon />

                <div className="flex flex-col">
                  <div className="font-medium">{action.title}</div>
                  <div className="text-sm text-muted-foreground">
                    {action.description}
                  </div>
                </div>
              </Link>
            ))}
          </nav>
          <h3 className="text-lg font-medium">
            {t('dashboard.recentMindMaps.title')}
          </h3>
          {isError && <Error />}
          {!userLessonsLoading &&
          !isError &&
          Array.isArray(userLessons) &&
          userLessons.length < 1 ? (
            <Placeholder className="text-lg text-muted-foreground h-64 flex items-center justify-center gap-4 border-gray-300 dark:border-gray-700">
              <BookOpen className="w-8 h-8" />
              {t('dashboard.recentMindMaps.noMindMaps')}
            </Placeholder>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] lg:grid-cols-[repeat(auto-fill,minmax(400px,1fr))] gap-4">
              {userLessonsLoading ? (
                <MultiSkeleton
                  count={8}
                  className="aspect-[10/9.8] lg:aspect-[10/9.2]"
                />
              ) : (
                Array.isArray(userLessons) &&
                userLessons.length > 0 &&
                userLessons.map((lesson) => {
                  if (!lesson.lesson_id) return null
                  return (
                    <GetLessonListItem
                      key={lesson.id}
                      lessonId={lesson.lesson_id}
                    />
                  )
                })
              )}
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  )
}
